import React from 'react';
/**
 * The one non-registry mark in this file, and the first `~/components/ui` import in `v1_5/`.
 *
 * It is here because the working mark is a LIVE component, not an SVG — see `STATE_ICON` — and the
 * alternative was re-drawing a 25-dot matrix inside this folder, which is how a product ends up with
 * two thinking indicators that drift apart. `DeckIcon` still draws all fourteen of the others.
 */
import { DotmSquare11 } from '~/components/ui/dotm-square-11';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import styles from './ActivityGlyph.module.css';
import type { ActivityTool, ChatTheme, ItemState } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The lifecycle marks a single agent action can wear, on top of the three per-item states.
 *
 * `ItemState` (`done` | `running` | `pending`) is the ladder inside an `ActivityList`; the seven
 * added here are the terminals a whole action reaches. They live in this file rather than in
 * `theme.ts` because nothing else in the chat needs to name them — every other part takes a
 * rendered `<ActivityGlyph/>` as a node, never the state that chose it.
 */
export type ActivityState =
  | ItemState
  | 'complete'
  | 'accepted'
  | 'rejected'
  | 'failed'
  | 'deleting'
  | 'deleted'
  | 'stopped';

/**
 * The chrome a control draws about *itself* — disclosure, dismissal, confirmation, "opens
 * elsewhere". The third axis, and the only one that says nothing about the content.
 *
 * It exists because three build agents independently reached past this atom for a chevron and each
 * wrapped it in a private `.chevron` span carrying its own tone — which is exactly the duplication
 * the file exists to prevent. `tool` says what a row IS and `state` says how it is GOING; a
 * chevron is neither, so both unions were right to refuse it and the answer was a third union
 * rather than a hole.
 *
 * Named for what the mark means, never for the file it lands on: `chevron-right`, not
 * `arrow-right-s`; `external`, not `arrow-up-right`, because which way the arrow points is an
 * implementation detail of "this opens somewhere else".
 *
 * `chevron-left` joined `chevron-right` when the question card grew a pager (Beka, 2026-09-20).
 * It is the same argument the rest of the union is built on — which way a navigation chevron points
 * is furniture on the control, not content — and a card that reached past this atom for its back
 * arrow while using it for its x would be the private `.chevron` span this union exists to prevent.
 *
 * Seven members and no eighth. The raw `DeckIcon` calls still standing in this folder are all marks
 * that ARE the content — the composer's plus and send, the action bar's copy/like/retry, the menu's
 * 18px leads, `RegionReference`'s cursor, `MenuSearchField`'s magnifier — and pulling those in
 * would make this union the registry with extra steps.
 */
export type ActivityChrome =
  | 'chevron-up'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'external'
  | 'check'
  | 'close';

/** The five glyph sizes the 80 artboards use. There is no sixth — see the ladder below. */
export type ActivityGlyphSize = 12 | 14 | 15 | 16 | 17;

/**
 * The tone ramp a glyph can be painted at, named for the token it resolves to.
 *
 * `inherit` is the odd member and the load-bearing one: it paints nothing, so the mark takes the
 * `color` of whatever it was dropped into. Every column spec says a row's tone moves "icon and
 * label together", and a glyph that paints its own colour makes that impossible — inheritance from
 * the row can never beat a `color` declaration sitting on the mark. It is also what keeps a hover
 * lift in one rule: `ArtifactCard`'s chevron goes muted -> subtle on the card's `:hover`, and that
 * is one fact about the card, not two tones threaded back through a prop.
 */
export type ActivityGlyphTone =
  | 'default'
  | 'subtle'
  | 'muted'
  | 'hint'
  | 'success'
  | 'error'
  | 'accent'
  | 'inherit';

export interface ActivityGlyphProps {
  /**
   * Exactly one of the three. `tool` is identity (what the row IS), `state` is lifecycle (how it is
   * going), `chrome` is the control's own furniture (what happens if you press it).
   *
   * Pass more than one and the precedence is `tool` > `state` > `chrome` — the existing
   * identity-over-lifecycle rule with chrome added under both, because chrome is the one axis that
   * describes the button rather than the thing the button is about.
   */
  tool?: ActivityTool;
  state?: ActivityState;
  chrome?: ActivityChrome;
  /**
   * 12 | 14 | 15 | 16 | 17 only — the sizes the design uses, and the whole ladder:
   * **17** ArtifactCard / TaskCard lead, chevron-right, menu check ·
   * **16** list-row lead, widget lead, action bar, arrow-up-right, icon-led paragraph lead,
   * ModelPill chevron-down, mention marks and the x they swap to ·
   * **15** collapsed-step lead, chevron-up, stop square, user hover actions ·
   * **14** collapsed-step chevron-down, cursor pointer, queue close, BubbleFooter's chevron ·
   * **12** the AttachmentCard remove x, and nothing else in eighty artboards.
   *
   * 12 is the rung INVENTORY §1.1 does not list, and the reason is worth keeping: that ladder
   * inventories the marks that sit on a *text line*, and this one sits inside a 20px dot pinned to
   * a card corner (§1.12 — `x 12×12 --cv15-text-subtle`). It is a real drawn size, in the icon
   * table at §4 with the other two x's, so it belongs here rather than in a private wrapper that
   * would put the fifth rung outside the one file that owns the ladder.
   */
  size?: ActivityGlyphSize;
  /**
   * Overrides the tone the tool/state would pick. Used by Deleting (spinner at hint), and by every
   * `chrome` call site — chrome has no tone of its own, see the branch in the body.
   */
  tone?: ActivityGlyphTone;
  /**
   * Whether the mark is attached to something still in flight. Only the `agent` dot matrix reads
   * it — and for that mark it decides not merely whether to breathe but whether it is DRAWN, so
   * this is the one prop in the file that can remove a glyph rather than restyle it. See the gate
   * on `rippling` in the body for why a matrix standing still is worse than no mark at all.
   *
   * It defaults off, which now means a `tool="agent"` row draws no mark unless it says it is
   * running. That is the right default in both directions: a settled artboard does not have to
   * switch an animation back off, and a live one cannot forget to switch the mark on without also
   * losing the motion that was the point of it.
   */
  live?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/**
 * `tool` -> registry name. The one place a semantic tool name becomes an SVG.
 *
 * Three of these are traps the icon audit caught and they are commented where they sit, because
 * every one of them has a plausible-looking wrong answer sitting next to it in the registry.
 */
const TOOL_ICON: Record<ActivityTool, string> = {
  /* The presentation screen on three feet. Queue's transcription called it a "keyboard" and
     agent-tool's called it a "monitor" — one glyph, three names. `presentation.svg` and
     `presentation-view.svg` are different drawings; do not reach for them. */
  artifact: 'slides',
  /* The dog-eared page with two rules. `file-2.svg` is the body path with the rules missing. */
  document: 'file-text',
  'canvas-edit': 'edit',
  /* `brand-kit.svg`, the kit's own mark, and not `palette.svg`: the row is about the kit as a thing
     the user owns, not about the act of choosing a colour. The kit page leads with the same drawing. */
  'brand-kit': 'brand-kit',
  /* The numbered list. `outline.svg` is a *bulleted* glyph — a different drawing entirely. */
  outline: 'number-list',
  goal: 'goal',
  /* Research leads with the globe, not the magnifier: the row is about where the claim came from,
     not about the act of searching. */
  research: 'global',
  image: 'image',
  /* The registry's own upload mark: the same picture with an up arrow at bottom-right. */
  'image-add': 'image-upload',
  /* Authored for this design: four frame corners around an inner rounded square. Nothing in the
     registry read as "into a slot" — `zoom-to-fit.svg` is a diagonal arrow in a box, which is a
     view operation, not a placement. */
  'image-place': 'image-place',
  /* Authored: `image.svg`'s outline with a minus at bottom-right, the mirror of `image-upload`. */
  'image-remove': 'image-minus',
  clock: 'clock',
  chat: 'chat-1',
  /* Authored for this design. The caption on *Agent draft* is a hard rule — "leads with the dot matrix,
     never a sparkle" — and the registry's `agent.svg` IS a sparkle. */
  agent: 'agent-dots',
  /* Writing to the agent's memory reads as a thing CONFIRMED, so both of these take the check: the
     point card is stored, the skill is loaded and in hand. The same drawing `state: 'done'` uses,
     and deliberately a separate door to it — done is a step the system finished, these are what the
     row is about. */
  'point-card-set': 'check',
  skill: 'check',
  /* Reading is an eye, and it is the same eye for both because they are the same act on two
     objects: look at what the deck is supposed to say, look at the deck the way a stranger would. */
  'point-card-read': 'eye',
  'stranger-read': 'eye',
  /* `brain.svg`, and it is the one mark here that names a faculty rather than an object — because
     the row does too. It is NOT the dot matrix: that one means the model is working, and this row
     only ever exists once it has stopped. */
  thinking: 'brain',
  /* `linear.svg` is the wrong asset (a 100-unit viewBox drawn at 15x15). The 14-unit brand mark
     the design uses is `linear-logo.svg`. */
  linear: 'linear-logo',
  figma: 'figma',
  notion: 'notion',
  github: 'github',
};

/**
 * `state` -> registry name.
 *
 * **`running` and `deleting` are the dot matrix, not `loop-right`.** They were the two-arc spinner
 * until Guri called it: *"don't use that circle refresh icon anywhere — use the thinking indicator
 * UI instead. Literally every state should be that."* The arcs read as *refresh*, and nothing in the
 * chat refreshes; the 25-circle diamond already means "work is happening" everywhere else in the
 * system, so the two working marks are now one drawing. This map is the only place that had to
 * change for every consumer to follow.
 *
 * **`agent-dots` here names a COMPONENT, not a file, and that is the second half of the same
 * instruction.** The first pass pointed these two states at `agent-dots.svg` and Guri rejected it
 * again — *"this is not the ThinkingIndicator UI we used everywhere"* — because the SVG is a still
 * drawing that the wrapper fades in and out as one block, while the real mark is `DotmSquare11`,
 * whose 25 dots ripple outward from the centre by Manhattan distance. Same diamond, different
 * animal, and the difference is visible at a glance. The body branches on this name and renders the
 * component; the string stays because it is what `TOOL_ICON.agent` and the registry still call it.
 *
 * The motion follows the drawing rather than the state — see `spins` / `rippling` in the body. A
 * rotating diamond is a diamond spinning; the matrix ripples instead, the way it does on *Thinking*.
 *
 * The one thing the collapse costs is the identity split the file header describes: the dot matrix
 * used to be the only mark that meant *the model itself*, with the spinner meaning *this tool*. That
 * distinction now lives in the tone (`subtle` for a running step, `muted` for an agent lead) and in
 * the label beside it, which is where the design already carried most of it.
 */
const STATE_ICON: Record<ActivityState, string> = {
  done: 'check',
  running: 'agent-dots',
  /* The plain ring. NOT `plan-pending.svg`, which is a dashed 15px ring from another surface. */
  pending: 'circle',
  complete: 'check-circle',
  /* Same two paths as `complete`. Only the tone separates the two — see `STATE_TONE`. */
  accepted: 'check-circle',
  rejected: 'close',
  failed: 'error-warning',
  /* The same matrix as `running`, dimmer: the thing is on its way out, not on its way in. */
  deleting: 'agent-dots',
  deleted: 'delete-bin-6',
  stopped: 'stop-square',
};

/**
 * The tone each state picks when the caller does not override it, verbatim from the eight
 * `Agent state` artboards and the three-state row ladder in `agent-tool`.
 *
 * Read down the column and the rule is legible: `subtle` is work the system is doing, `muted` is
 * work it finished, `hint` is everything that has left the user's hands — rejected, deleting,
 * deleted, stopped, and the pending row that has not started. Green is Accepted alone and red is
 * Failed alone; nothing else in 80 artboards is either colour.
 */
const STATE_TONE: Record<ActivityState, ActivityGlyphTone> = {
  done: 'muted',
  running: 'subtle',
  pending: 'hint',
  complete: 'subtle',
  accepted: 'success',
  rejected: 'hint',
  failed: 'error',
  deleting: 'hint',
  deleted: 'hint',
  stopped: 'hint',
};

/**
 * `chrome` -> registry name. Six marks about the control, none about its content.
 *
 * The two vertical chevrons are separate files rather than one glyph rotated 180deg, and that is
 * the design's call, not an oversight: `arrow-up-s`'s apex sits half a pixel off a rotation of
 * `arrow-down-s`. Carrying the direction as a union member is what keeps that from being
 * re-litigated in every component that draws a disclosure.
 */
const CHROME_ICON: Record<ActivityChrome, string> = {
  'chevron-up': 'arrow-up-s',
  'chevron-down': 'arrow-down-s',
  /* Separate files rather than one rotated, for the reason the vertical pair above carries. */
  'chevron-left': 'arrow-left-s',
  'chevron-right': 'arrow-right-s',
  /* The registry file reads right-then-up, the design's own annotation reads arrow-up-right, and
     the member is named for neither — what the mark says is "this opens elsewhere". */
  external: 'arrow-right-up',
  /* The same file `state: 'done'` lands on, and deliberately a separate door to it: a menu check is
     a choice the user made, a done check is a step the system finished, and they are drawn at
     different tones (`--cv15-text` vs muted) because of it. */
  check: 'check',
  /* Likewise `state: 'rejected'`. Dismissing a queued message is not a run that got rejected. */
  close: 'close',
};

/**
 * `inherit` maps to `undefined`, and that is the entire mechanism: no class is emitted, `.glyph`
 * declares no `color` of its own, so the mark takes its parent's. Every other member is one class
 * carrying one declaration.
 */
const TONE_CLASS: Record<ActivityGlyphTone, string | undefined> = {
  default: styles.toneDefault,
  subtle: styles.toneSubtle,
  muted: styles.toneMuted,
  hint: styles.toneHint,
  success: styles.toneSuccess,
  error: styles.toneError,
  accent: styles.toneAccent,
  inherit: undefined,
};

/**
 * The brand marks that keep their own colours.
 *
 * Notion and GitHub are deliberately absent — they are the two marks that go through the tone ramp
 * like any UI glyph, so `DeckIcon` rewrites their fills to `currentColor`.
 *
 * Notion is the designer's own choice: the *Notion* card draws the mark at
 * `--color-text-white-muted`, not in Notion black. GitHub follows it by Beka's call — `#24292F` on
 * `--cv15-ground-1` (~#141415) is roughly 1.1:1, so the mark as authored is invisible on exactly
 * the ground the *Github* card and *Outside the deck* put it on. Tinting it matches a decision the
 * design had already made for its neighbour rather than inventing a rule for one icon.
 */
const PRESERVE_COLORS: ReadonlySet<ActivityTool> = new Set<ActivityTool>(['linear', 'figma']);

/**
 * The one place a semantic tool/state/chrome name becomes (registry icon, px size, tone token,
 * `preserveColors`).
 *
 * Every leading glyph, every row status mark and every piece of navigational chrome in the chat
 * goes through it, so the size ladder and the tone ladder each exist exactly once. Nothing else in
 * the system should ever name a registry string — pass a `tool`, a `state` or a `chrome` and let
 * this decide.
 *
 * The glyph is decorative: it always sits beside a label that says the same thing in words, so it
 * is `aria-hidden` and takes no accessible name. A row that changes state while the user is
 * looking away announces that through its own `aria-live` region, not through the mark.
 */
export function ActivityGlyph({
  tool,
  state,
  chrome,
  size = 16,
  tone,
  live = false,
  theme: themeProp,
  className,
}: ActivityGlyphProps) {
  const theme = useV15Theme(themeProp);
  let name: string | null = null;
  // `tool` > `state` > `chrome` if more than one is somehow passed: identity outranks lifecycle —
  // a Linear row that lost its brand mark is a worse failure than one that lost its spinner — and
  // both outrank chrome, which is the only axis that is not about the row's content at all.
  let derivedTone: ActivityGlyphTone = 'muted';
  if (tool) {
    name = TOOL_ICON[tool];
    // Every leading tool mark in every artboard is `--color-text-white-muted`. There is no second
    // tone for identity anywhere in the file.
    derivedTone = 'muted';
  } else if (state) {
    name = STATE_ICON[state];
    derivedTone = STATE_TONE[state];
  } else if (chrome) {
    name = CHROME_ICON[chrome];
    /* Chrome is the one axis with no tone of its own, and that is a finding rather than a gap: the
       same chevron-right is muted on `ArtifactCard` and muted in a menu row, the same x is subtle
       on an attachment, hint in the queue and accent on a mention, and `TaskCard`'s arrow rests a
       rung below the chevron it replaces. The mark belongs to the control, so the control's colour
       is the only defensible default — and it means a chrome glyph whose caller forgot the tone
       lifts with its row's hover instead of sitting frozen at a tone nobody chose. */
    derivedTone = 'inherit';
  }
  if (!name) return null;

  const brand = tool !== undefined && PRESERVE_COLORS.has(tool);
  // A brand mark ignores tone by construction: its fills are never rewritten to `currentColor`,
  // so setting `color` on the box would have no effect to begin with.
  const resolvedTone = brand ? 'muted' : tone ?? derivedTone;
  // Continuous, not a reveal — these are the only glyphs in the system that carry motion of their
  // own. Both gates read *liveness*, never identity: the mark says what a row is, and whether that
  // row is still moving is a separate question. Hence `live` rather than `tool === 'agent'` alone —
  // a `state` can be running too, and it is the running that draws the mark, never the agent.
  const working = !tool && (state === 'running' || state === 'deleting');
  // Keyed off the DRAWING, not off the axis that chose it: `running` is now the dot matrix, and a
  // matrix put through `.spin` is a diamond rotating — which reads as a loading throbber from some
  // other product, not as the mark this chat already uses for work in progress. It ripples instead.
  // `.spin` is kept for any state that goes back to a rotational mark; today none does.
  const matrix = name === TOOL_ICON.agent;
  const spins = working && !matrix;
  const rippling = matrix && (live || working);
  /*
   * A matrix that is not moving is not a mark.
   *
   * It is the one glyph in this file that is a LOADING drawing rather than a picture of a thing —
   * `DotmSquare11` announces itself `role="status"` / "Loading" — so standing still it does not
   * read as the agent's identity, it reads as a spinner that stopped. Beka, 2026-09-20, on a
   * settled `Thought for 12 sec`: "it has this kind of frozen icon in front of it … it looks like
   * it's broken." That retires the *Agent draft* reading above: the same 25 circles at rest on a
   * finished row were never saying *the model did this*, they were saying *still going*.
   *
   * It is also the only mark that can be dropped without losing anything, because it is the only
   * one that never distinguished its rows from each other — `tool: 'agent'` is the thinking recap,
   * the point card, the stranger read, the cookbook and every skill, so the mark said no more than
   * the sentence beside it already did. A settled row now simply leads with its label.
   *
   * Only the STATIC case goes. A running row still ripples, and that is the whole signal.
   */
  if (matrix && !rippling) return null;

  return (
    <span
      className={cn(
        chatThemeClass(theme),
        styles.glyph,
        TONE_CLASS[resolvedTone],
        tool === 'figma' && styles.figma,
        spins && styles.spin,
        className
      )}
      // The only runtime value in the component, so the only thing that may be an inline style.
      style={{ '--cv15-glyph-size': `${size}px` } as React.CSSProperties}
      aria-hidden="true"
    >
      {matrix ? (
        /*
         * The live matrix, and NOT wrapped in `.shimmer`.
         *
         * `animated` is the whole motion story here: on it ripples, off it renders the same 25 dots
         * at the resting opacity ramp the resolver falls back to. Stacking `.shimmer` on top would
         * fade the ripple in and out as one block — two animations describing the same fact, which
         * is what a mark that is "breathing while it breathes" looks like. (`.shimmer` would in fact
         * no-op, since it selects `.deck-icon` and there is none here; leaving it on would be a rule
         * that survives only by missing.)
         *
         * `dotSize={2}` at every rung. The matrix is 5 dots across, so the cell is `size / 5` —
         * 3.2px at 16, 3px at 15 — and a 2px dot leaves the gap the dot needs to still read as a
         * dot. It matches `ThinkingIndicator`, which is the mark this one is being made identical to.
         *
         * `currentColor` is the component's own default, so the tone class on the span above still
         * decides the colour exactly as it does for a `DeckIcon`.
         */
        <DotmSquare11 size={size} dotSize={2} animated={rippling} />
      ) : (
        <DeckIcon name={name} size={size} preserveColors={brand} />
      )}
    </span>
  );
}
